import React, { forwardRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useToolGuideSync from "../../../../../features/toolGuide/useToolGuideSync";
import { BUCKET_KEYS } from "../../../../../features/toolGuide/toolGuideConfig";

/**
 * Salesperson Project Approved: "Accept" (POST /project-assigned/direct-accept) above "Forward"
 * (POST /project-assigned). Optional "Accept All" accepts every pending manager on the lead.
 * Parent builds identical req.body for Accept/Forward; parent handles click-outside.
 */
const ActionForwardApproved = forwardRef(function ActionForwardApproved(
  {
    menuKey,
    openMenuKey,
    setOpenMenuKey,
    disabled = false,
    isForwarded,
    isAccepted = false,
    onAccept,
    onAcceptAll,
    onForward,
    onCancelForwarded,
    childrenAfterForward,
    newManagerMode = false,
    rootClassName = "w-[80%]",
    showAccept = true,
    showAcceptAll = false,
  },
  ref
) {
  const isOpen = openMenuKey === menuKey;
  const { syncToolGuideStep } = useToolGuideSync(BUCKET_KEYS.PROJECT_APPROVED);

  const toggle = () => {
    if (disabled) return;
    setOpenMenuKey(isOpen ? null : menuKey);
  };

  const close = () => setOpenMenuKey(null);

  const buttonLabel = newManagerMode
    ? "Select Option"
    : isAccepted
      ? "Accepted"
      : isForwarded
        ? "Forwarded"
        : "Select Option";

  return (
    <div
      ref={ref}
      className={`relative flex p-0 rounded-xl align-middle items-center ${rootClassName}`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={toggle}
        className={`relative border w-full text-xs border-gray-400 text-nowrap rounded-md p-2 shadow-md text-start overflow-hidden outline-none ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
      >
        {buttonLabel}
        <ExpandMoreIcon
          className={`absolute top-1 right-0 bg-white ${disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer group-hover:bg-gray-100"}`}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            toggle();
          }}
        />
      </button>
      {isOpen && !disabled && (
        <div
          style={{ zIndex: 9999 }}
          className="text-start bg-white absolute top-full left-0 w-full p-1 cursor-pointer overflow-y-auto text-xs border border-gray-300 shadow-lg flex flex-col gap-1"
        >
          {newManagerMode && onForward && (
            <>
              {showAccept && onAccept && (
                <div
                  className="p-1 hover:bg-gray-200 cursor-pointer"
                  onClick={() => {
                    syncToolGuideStep("accept");
                    close();
                    onAccept();
                  }}
                >
                  Accept
                </div>
              )}
              <div
                className="p-1 hover:bg-gray-200 cursor-pointer"
                onClick={() => {
                  syncToolGuideStep("forward");
                  close();
                  onForward();
                }}
              >
                Forward
              </div>
            </>
          )}

          {!newManagerMode && !isForwarded && onAccept && onForward && (
            <>
              <div
                className="p-1 bg-white hover:bg-gray-200 cursor-pointer"
                onClick={() => {
                  syncToolGuideStep("accept");
                  close();
                  onAccept();
                }}
              >
                Accept
              </div>
              <div
                className="p-1 bg-white hover:bg-gray-200 cursor-pointer"
                onClick={() => {
                  syncToolGuideStep("forward");
                  close();
                  onForward();
                }}
              >
                Forward
              </div>
              {showAcceptAll && onAcceptAll && (
                <div
                  className="p-1 bg-white hover:bg-gray-200 cursor-pointer"
                  onClick={() => {
                    close();
                    onAcceptAll();
                  }}
                >
                  Accept All
                </div>
              )}
            </>
          )}

          {!newManagerMode && childrenAfterForward}

          {!newManagerMode && isForwarded && onCancelForwarded && (
            <div
              onClick={() => {
                syncToolGuideStep("cancel");
                close();
                onCancelForwarded();
              }}
              className="p-1 bg-white hover:bg-gray-200 cursor-pointer"
            >
              Cancel
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ActionForwardApproved;
